import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Shield, User, Loader2, Eye, EyeOff, Key, UserCheck, UserX, Users as UsersIcon, Link as LinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { usersApi, rolesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const UsersPage = () => {
  const { user: currentUser, isOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role_id: ''
  });

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        usersApi.getAll(),
        rolesApi.getAll()
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para ver usuarios');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      fetchData();
    }
  }, [isOwner]);

  const handleOpenCreate = () => {
    setSelectedUser(null);
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      role_id: roles[0]?.id || ''
    });
    setShowPassword(false);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.full_name || !formData.email || !formData.role_id) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Ingresa un email válido');
      return;
    }

    setSubmitting(true);
    try {
      await usersApi.create(formData);
      toast.success('Usuario creado');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating user:', error);
      let errorMessage = 'Error al crear usuario';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Usuario desactivado' : 'Usuario activado');
      fetchData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al actualizar usuario');
    }
  };

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      await usersApi.update(userId, { role_id: newRoleId });
      toast.success('Rol actualizado');
      fetchData();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar rol');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Ingresa una nueva contraseña');
      return;
    }

    try {
      await usersApi.resetPassword(selectedUser.id, newPassword);
      toast.success('Contraseña restablecida');
      setPasswordModalOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Error al restablecer contraseña');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await usersApi.delete(selectedUser.id);
      toast.success('Usuario eliminado');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  if (!isOwner) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="p-6 lg:p-8">
            <div className="admin-card text-center py-16">
              <Shield size={48} className="mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-xl font-black uppercase mb-4">Acceso Restringido</h3>
              <p className="text-[#A1A1AA]">
                Solo el administrador principal puede gestionar usuarios.
              </p>
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="p-6 lg:p-8 space-y-6"
          data-testid="users-manager"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight">
                Admin Users
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                Gestiona los accesos del equipo
              </p>
            </div>
            <Button 
              onClick={handleOpenCreate}
              className="admin-btn-primary"
              data-testid="create-user-btn"
              disabled={roles.length === 0}
            >
              <Plus size={18} className="mr-2" />
              Nuevo Usuario
            </Button>
          </motion.div>

          {/* Stats Row */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <UsersIcon size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Total</p>
                  <p className="metric-value-gold text-xl">{users.length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <UserCheck size={20} className="text-green-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Activos</p>
                  <p className="text-xl font-bold text-green-400">{users.filter(u => u.is_active).length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <UserX size={20} className="text-red-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Inactivos</p>
                  <p className="text-xl font-bold text-red-400">{users.filter(u => !u.is_active).length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Shield size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Roles</p>
                  <p className="text-xl font-bold text-blue-400">{roles.length}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {roles.length === 0 && !loading && (
            <motion.div variants={itemVariants} className="admin-card border-yellow-500/30 bg-yellow-500/5 p-4">
              <p className="text-yellow-500 text-sm">
                ⚠️ Crea al menos un rol antes de agregar usuarios.
              </p>
            </motion.div>
          )}

          {/* Users Table */}
          <motion.div variants={itemVariants}>
            {loading ? (
              <div className="admin-card p-0 overflow-hidden">
                <div className="space-y-0">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 skeleton" />
                  ))}
                </div>
              </div>
            ) : users.length === 0 ? (
              <div className="admin-card text-center py-16">
                <User size={48} className="mx-auto mb-4 text-[#52525B]" />
                <h3 className="text-xl font-black uppercase mb-4">Sin usuarios</h3>
                <p className="text-[#A1A1AA]">Solo existe el administrador principal.</p>
              </div>
            ) : (
              <div className="admin-card overflow-hidden p-0">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Email / Portal</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th>Último Acceso</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              user.is_owner ? 'bg-[#D4AF37]/20' : 'bg-[#27272A]'
                            }`}>
                              {user.is_owner ? (
                                <Shield size={18} className="text-[#D4AF37]" />
                              ) : (
                                <User size={18} className="text-[#71717A]" />
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-white">{user.full_name}</p>
                              <p className="text-xs text-[#52525B] font-mono">@{user.username}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          {user.email ? (
                            <div>
                              <p className="text-sm text-zinc-300">{user.email}</p>
                              <p className="text-[10px] text-emerald-400/80 flex items-center gap-1 mt-0.5">
                                <LinkIcon size={10} />
                                Acceso via Mi Cuenta
                              </p>
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-600">Sin email</span>
                          )}
                        </td>
                        <td>
                          {user.is_owner ? (
                            <span className="badge-gold">OWNER</span>
                          ) : (
                            <Select 
                              value={user.role_id} 
                              onValueChange={(value) => handleRoleChange(user.id, value)}
                            >
                              <SelectTrigger className="w-36 bg-transparent border border-[#27272A] rounded-md h-8 text-sm hover:border-[#D4AF37]/50 transition-colors">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121212] border border-[#27272A]">
                                {roles.map(role => (
                                  <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td>
                          <span className={user.is_active ? 'badge-success' : 'badge-error'}>
                            {user.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <span className="font-mono text-sm text-[#71717A]">
                            {user.last_login 
                              ? format(new Date(user.last_login), 'd MMM, HH:mm', { locale: es })
                              : 'Nunca'}
                          </span>
                        </td>
                        <td>
                          {!user.is_owner && (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(user)}
                                title={user.is_active ? 'Desactivar' : 'Activar'}
                                className="h-8 w-8 p-0 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
                              >
                                {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setPasswordModalOpen(true);
                                }}
                                title="Restablecer contraseña"
                                className="h-8 w-8 p-0 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
                              >
                                <Key size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </motion.div>

      {/* Create User Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <Plus size={20} className="text-[#D4AF37]" />
              </div>
              Nuevo Usuario
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                Nombre de Usuario *
              </Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                placeholder="nombre_usuario"
                required
                className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-md"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                Contraseña *
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  required
                  className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-md pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#D4AF37] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                Nombre Completo *
              </Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Juan Pérez"
                required
                className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-md"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                Email *
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@ejemplo.com"
                required
                className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-md"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
                Rol *
              </Label>
              <Select 
                value={formData.role_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, role_id: value }))}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-md">
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="admin-btn-primary">
                {submitting ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Crear Usuario'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="bg-[#121212] border border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <Key size={20} className="text-[#D4AF37]" />
              </div>
              Restablecer Contraseña
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[#71717A] mb-4 text-sm">
              Usuario: <strong className="text-white">{selectedUser?.username}</strong>
            </p>
            <Label className="text-xs uppercase tracking-widest text-[#71717A] mb-2 block">
              Nueva Contraseña
            </Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] rounded-md"
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPasswordModalOpen(false)} className="hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} className="admin-btn-primary">
              Restablecer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#121212] border border-[#27272A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-black uppercase">
              ¿Eliminar usuario?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              Esta acción no se puede deshacer. El usuario perderá acceso al sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="admin-btn-secondary">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default UsersPage;
